import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { ProductDetailSectionHeading } from './ProductDetailSectionHeading';
import { ProductDetailField } from './ProductDetailField';
import { StockScopeNote } from './StockScopeNote';

type ProductDetailBodySkeletonProps = {
    /** Whose stock the figures will count, which the note under Variants names. */
    stockFigures: StockFiguresScope;
};

/** The summary card's fields, in the loaded card's order. */
const SUMMARY_FIELDS = ['sku', 'unit', 'vat', 'category', 'brand'] as const;

/**
 * The details page's content while the product loads (rulebook §12,
 * bos-app#139): the summary card's labels and the Variants heading and note
 * are static, so they show at once; the values, the variant count and the
 * variant rows are placeholders in their place, so nothing shifts on load.
 * The header row above is live meanwhile.
 */
export const ProductDetailBodySkeleton: React.FC<
    ProductDetailBodySkeletonProps
> = ({ stockFigures }) => {
    const { t } = useTranslation('pages');

    return (
        <div
            aria-busy
            className="flex flex-col gap-6"
            {...getNodeTestIdProp('product-detail-body-loading')}
        >
            <Widget className="flex flex-col gap-4 p-5">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                    {SUMMARY_FIELDS.map((field) => (
                        <ProductDetailField
                            key={field}
                            label={t(`products.detail.summary.${field}`)}
                            slot={field}
                        >
                            <Skeleton className="h-4 w-24 rounded-md" />
                        </ProductDetailField>
                    ))}
                </dl>
            </Widget>
            <section className="flex flex-col gap-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <div className="flex items-center gap-2">
                        <ProductDetailSectionHeading>
                            {t('products.detail.variants.title')}
                        </ProductDetailSectionHeading>
                        <Skeleton className="h-4 w-6 rounded-md" />
                    </div>
                    <StockScopeNote stockFigures={stockFigures} />
                </div>
                <div className="flex flex-col gap-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton
                            className="h-12 w-full rounded-xl"
                            key={`product-detail-body-loading-variant-${index}`}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
};
